import { PlusOutlined } from "@ant-design/icons";
import { Button, Divider, Flex, Select, Spin } from "antd";
import dayjs from "dayjs";
import type { UIEvent } from "react";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import PickerListFooter from "../../components/ClientPicker/PickerListFooter.component";
import { EhrSyncButton, EhrSyncStatus, useEhrSync } from "../../components/EhrSyncButton";
import { useRootPath } from "../../hooks/app.hook";
import { useAppDispatch, useAppSelector } from "../../hooks/redux-hooks";
import { useBreakpointView, useIsMobileView } from "../../hooks/ui-hook";
import { useClientBrowse } from "../../hooks/useClientBrowse";
import { useClientSearch } from "../../hooks/useClientSearch";
import useSpecialityReference from "../../hooks/useSpecialityReference";
import {
  setCreateClientState,
  setCustomiseSectionState,
  setLoadingNotesState,
  setNotesViewVisible,
  setSelectedClient,
} from "../../slices/appStateSlice";
import { resetClientForm, setIsEditing } from "../../slices/clientSlice";
import { resetNotesDetails, setNotesDetails } from "../../slices/notesSlice";
import type { TClientForm } from "../../types/index.type";
import styles from "./home.module.scss";

const ClientSelector = () => {
  const componentName = "home";
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const isMobile = useIsMobileView();

  const { rootPath } = useRootPath();

  const { referIndividualBy } = useSpecialityReference();

  const { isDemoPath } = useAppSelector((state) => state.appState);
  // The sidebar (ClientView) holds Sync above 768px and is hidden below it.
  const isPhoneWidth = useBreakpointView(768);
  const canSync = useAppSelector((state) => state.auth.userInfo.can_sync_ehr_patients);
  const sync = useEhrSync();
  const showSync = isPhoneWidth && !!canSync && !isDemoPath && !sync.hidden;
  // This picker mirrors the sidebar's "All" tab (owned + shared), so browse + search are both
  // scope=all. Browsing reads the sidebar's section (one source); search is a server overlay.
  const { query, results, loading, error, belowMinChars, onSearch, retry, reset } = useClientSearch("all");
  const { browseClients, browseLoading, browseHasMore, browseError, ensureLoaded, loadMore, retryBrowse } =
    useClientBrowse("all");

  const isBrowsing = !query.trim();
  const sourceClients: TClientForm[] = isBrowsing ? browseClients : results;

  // Infinite scroll inside the dropdown: nearing the bottom advances the shared cursor,
  // extending the sidebar's list in lockstep. Search results are a single-page overlay.
  const onPopupScroll = useCallback(
    (e: UIEvent<HTMLDivElement>) => {
      if (!isBrowsing || !browseHasMore || browseLoading) return;
      const el = e.currentTarget;
      if (el.scrollHeight - el.scrollTop - el.clientHeight < 60) loadMore();
    },
    [isBrowsing, browseHasMore, browseLoading, loadMore],
  );

  const clientOptions = useMemo(() => {
    return sourceClients.map((client) => {
      const formattedDob = client.date_of_birth ? dayjs(client.date_of_birth).format("MM/DD/YYYY") : "";
      const derivedAgeFromDob =
        client.date_of_birth && formattedDob ? dayjs().diff(dayjs(client.date_of_birth), "year") : null;
      const rawAge =
        typeof client.age === "number" ? client.age : client.age ? Number.parseInt(client.age.toString(), 10) : null;
      const displayAge = formattedDob ? derivedAgeFromDob : rawAge;

      const secondaryDetails: string[] = [];
      if (client.mrn) {
        secondaryDetails.push(`MRN/Patient ID: ${client.mrn}`);
      }
      if (formattedDob) {
        secondaryDetails.push(`DOB: ${formattedDob}`);
      } else if (displayAge !== null && !Number.isNaN(displayAge) && displayAge >= 0) {
        secondaryDetails.push(`Age: ${displayAge}`);
      }

      return {
        label: (
          <div style={{ display: "flex", flexDirection: "column" }}>
            <Flex align="center" gap={5}>
              <span>
                {client.name}{" "}
                {!client.is_owned && client.owner_name && (
                  <span style={{ fontSize: 12, color: "var(--ant-color-text-tertiary, #8c8c8c)" }}>
                    - Shared by {client.owner_name}
                  </span>
                )}{" "}
              </span>{" "}
            </Flex>
            {secondaryDetails.length > 0 && (
              <span style={{ fontSize: 12, color: "var(--ant-color-text-tertiary, #8c8c8c)" }}>
                {secondaryDetails.join(" · ")}
              </span>
            )}
          </div>
        ),
        value: client.id,
        client,
      };
    });
  }, [sourceClients]);

  const selectClient = useCallback(
    (clientId: number) => {
      // Resolve uuid from the chosen option (no in-memory list lookup, so off-page clients work).
      const client = clientOptions.find((o) => o.value === clientId)?.client;
      if (client?.uuid) navigate(`${rootPath}/clients/${client.uuid}`);
    },
    [clientOptions, navigate, rootPath],
  );

  const clearSelection = () => {
    if (!isDemoPath) {
      dispatch(setLoadingNotesState(true));
    }
    navigate(rootPath);
  };

  const showClientView = () => {
    const filterClient = document.querySelector(".filterClient");
    if (filterClient) {
      filterClient.classList.add("ant-select-dropdown-hidden");
    }
    dispatch(setCreateClientState(true));
    dispatch(setIsEditing(false));
    dispatch(resetClientForm());
    dispatch(setSelectedClient(null));
    dispatch(setNotesViewVisible(false));
    dispatch(setNotesDetails(null));
    dispatch(setCustomiseSectionState(false));
    dispatch(resetNotesDetails());
  };

  const emptyMessage = belowMinChars
    ? "Type 2+ letters to search by name…"
    : query.trim()
      ? `No ${referIndividualBy.toLowerCase()}s match "${query.trim()}"`
      : `No ${referIndividualBy.toLowerCase()}s yet — add your first below`;

  const listLoading = isBrowsing ? browseLoading && browseClients.length === 0 : loading;
  const listError = isBrowsing ? browseError && browseClients.length === 0 : error;

  return (
    <Flex vertical gap={10}>
      <Flex gap={10}>
        <Select
          showSearch
          placeholder={`Select a ${referIndividualBy.toLowerCase()}`}
          onChange={selectClient}
          onClear={clearSelection}
          allowClear
          listHeight={isMobile ? 66 : 256}
          placement="bottomLeft"
          filterOption={false}
          onSearch={onSearch}
          onDropdownVisibleChange={(open) => (open ? ensureLoaded() : reset())}
          onPopupScroll={onPopupScroll}
          loading={loading}
          notFoundContent={
            listLoading ? (
              <div style={{ textAlign: "center", padding: 8 }} aria-busy="true">
                <Spin size="small" />
              </div>
            ) : listError ? (
              <div style={{ padding: 8, color: "#cf1322" }}>
                ⚠ Couldn't load — check your connection.{" "}
                <Button type="link" size="small" style={{ padding: 0 }} onClick={isBrowsing ? retryBrowse : retry}>
                  Retry
                </Button>
              </div>
            ) : (
              <div style={{ padding: 8, color: "#8c8c8c" }}>{emptyMessage}</div>
            )
          }
          popupClassName="filterClient"
          dropdownRender={(menu) => (
            <div className="client">
              {menu}
              {isBrowsing && browseClients.length > 0 && (
                <PickerListFooter loadingMore={browseLoading} error={browseError} retry={retryBrowse} />
              )}
              <Divider style={{ margin: "4px 0" }} />
              <Flex gap={10}>
                <Button type="text" icon={<PlusOutlined />} onClick={showClientView}>
                  Add {referIndividualBy}
                </Button>
              </Flex>
            </div>
          )}
          style={{ flexGrow: 1 }}
          options={clientOptions}
        />
        {showSync && <EhrSyncButton sync={sync} />}
        <Button
          className={styles[`${componentName}__container-mobile-button`]}
          type="primary"
          onClick={showClientView}
          icon={<PlusOutlined />}
        >
          Add {referIndividualBy}
        </Button>
      </Flex>
      {showSync && <EhrSyncStatus sync={sync} />}
    </Flex>
  );
};

export default ClientSelector;
